/**
 * DetailPanelCard — canonical visual container for cards rendered on top of
 * the proposal detail panel's bg-secondary background.
 *
 * Every card in the detail area (action cards, trigger preview cards, agent
 * feed cards) should wrap its content in this component so the visual treatment
 * (radius, border, background, clip behavior) stays consistent across surfaces.
 *
 * This is a pure layout primitive — it accepts no business-logic props, no
 * header/footer structure, no variants beyond what className overrides provide.
 * Higher-level shells like ActionCardShell compose this for their specific needs.
 */

import type { ReactNode } from 'react';

import { cx } from '@/utils/cx';

interface DetailPanelCardProps {
  className?: string;
  children: ReactNode;
}

export function DetailPanelCard({ className, children }: DetailPanelCardProps) {
  return (
    <div
      className={cx(
        // `@container` establishes the container-query context every action
        // card's content sizes against. Card content is shared verbatim by
        // the full-page action route (~1000px column), the legacy
        // actions-centre detail pane (resizable) and the record sidebar
        // (min 400px), so insets must key off the CARD's width, not the
        // viewport — a `lg:` inset would fire on any wide screen even when
        // the column itself is 400px. See ActionCardContent.
        '@container',
        'rounded-xl overflow-clip bg-bg-primary border border-border-secondary',
        className
      )}
    >
      {children}
    </div>
  );
}
